import { query } from "@/composables/store";

export type UccSuggestMode = "debtor" | "secured_party";

export type UccSearchSuggestion = {
	value: string;
	label: string;
	city: string | null;
	state: string | null;
	filings: number | null;
	companyId: string | null;
	baseUrl: string | null;
};

export type UccSearchSuggestionResult = {
	suggestions: UccSearchSuggestion[];
	source: string | null;
	ranked: boolean;
};

type UccSearchSuggestionRecord = {
	value?: string | null;
	label?: string | null;
	city?: string | null;
	state?: string | null;
	filings?: number | string | null;
	company_id?: string | null;
	base_url?: string | null;
};

/** Prefixes shorter than this return nothing from the API, so they are not sent. */
export const UCC_SUGGEST_MIN_LENGTH = 2;
export const UCC_SUGGEST_DEBOUNCE_MS = 180;
export const UCC_SUGGEST_LIMIT = 8;

/** Only the party-name fields have a party-name index to complete from. */
export function uccSuggestionsSupported(field: string | null | undefined): boolean {
	return (field || "name") === "name";
}

/**
 * The prefix to complete: the entry being typed, which is the text after the last
 * `;` or line break when several names are pasted into one input.
 */
export function uccSuggestQueryFromInput(raw: string | null | undefined): string {
	const segments = String(raw ?? "").split(/[;\n]+/);
	const last = segments[segments.length - 1] ?? "";
	const collapsed = last.replace(/\s+/g, " ").trim();
	return collapsed.length >= UCC_SUGGEST_MIN_LENGTH ? collapsed : "";
}

function cleanText(value: unknown): string | null {
	const text = String(value ?? "")
		.replace(/\s+/g, " ")
		.trim();
	return text || null;
}

function toCount(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

export function toUccSearchSuggestion(record: UccSearchSuggestionRecord): UccSearchSuggestion | null {
	const value = cleanText(record.value);
	if (!value) return null;
	return {
		value,
		label: cleanText(record.label) || value,
		city: cleanText(record.city),
		state: cleanText(record.state),
		filings: toCount(record.filings),
		companyId: cleanText(record.company_id),
		baseUrl: cleanText(record.base_url),
	};
}

/** A picked suggestion may narrow the search only with a valid US-style state code. */
export function normalizeUccSuggestionState(value: unknown): string | null {
	const state = cleanText(value)?.toUpperCase() ?? "";
	return /^[A-Z]{2}$/.test(state) ? state : null;
}

/** "City, ST · 1,234 filings" — whichever parts are known. */
export function formatUccSuggestionDescription(suggestion: Pick<UccSearchSuggestion, "city" | "state" | "filings">): string {
	const parts: string[] = [];
	const location = [suggestion.city, suggestion.state].filter(Boolean).join(", ");
	if (location) parts.push(location);
	if (typeof suggestion.filings === "number" && suggestion.filings > 0) {
		parts.push(`${suggestion.filings.toLocaleString()} ${suggestion.filings === 1 ? "filing" : "filings"}`);
	}
	return parts.join(" · ");
}

/**
 * Replace the entry being typed with the picked name, keeping every entry that
 * was already committed. Returns the values to search with.
 */
export function uccValuesWithSuggestion(existingValues: string[], suggestionValue: string): string[] {
	const picked = suggestionValue.trim();
	const seen = new Set<string>();
	const next: string[] = [];
	[...existingValues, picked].forEach((item) => {
		const trimmed = item.trim();
		if (!trimmed) return;
		const key = trimmed.toLowerCase();
		if (seen.has(key)) return;
		seen.add(key);
		next.push(trimmed);
	});
	return next;
}

export async function fetchUccSearchSuggestions(
	mode: UccSuggestMode,
	q: string,
	options: { limit?: number; signal?: AbortSignal } = {}
): Promise<UccSearchSuggestionResult> {
	const response = await query<UccSearchSuggestionRecord>(
		"ucc/search-suggest",
		undefined,
		{ mode, q, limit: options.limit ?? UCC_SUGGEST_LIMIT },
		{ signal: options.signal }
	);
	const raw = response as typeof response & { source?: string | null; ranked?: boolean };
	const suggestions = (response.list || []).map(toUccSearchSuggestion).filter((item): item is UccSearchSuggestion => item !== null);
	return {
		suggestions,
		source: raw.source ?? null,
		ranked: raw.ranked !== false,
	};
}
